import { useEffect } from 'react'
import type { JSX } from 'react'

import {
  UpgradeDialog,
  Button,
  Text,
  List,
  Image,
  CheckIcon,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useUser } from '../../../../common/context/User'
import { Error as ErrorMessage } from '../../../../common/components/Error'
import useStartTrial from '../../../../common/hooks/useStartTrial'
import { prefixWithProduct } from '../../../../common/utils/prefixWithProduct'
import { getOrganization } from '../../../../common/utils/user'
import { getActiveProductFromPath } from '../../../../common/utils/getProduct'
import {
  getCommonTrackingPropertiesFromAccount,
  shouldPerformTrackingEvent,
} from '../../../../tracking/utils'
import trackFeatureTrialModalViewed from '../../../../tracking/FeatureTrialPaths/trackFeatureTrialModalViewed'
import useDismissObject from '../../../../common/hooks/useDismissObject'
import { MODALS } from '../../../../common/types'
import { useModalManager } from '../../../ModalManager/hooks/useModalManager'
import styles from './FeatureTrialModal.module.css'

type FeatureType = 'queueLimit' | 'approvals' | 'channelLimit'

type FeatureConfig = {
  title: string
  description: string
  benefits: string[]
  imageUrl: string
  imageAlt: string
}

const featureConfigs: Record<FeatureType, FeatureConfig> = {
  approvals: {
    title: 'Try approvals with Buffer, free for 14 days',
    description: 'With an upgraded Buffer plan, you can:',
    benefits: [
      'Review posts for quality and brand before hitting publish',
      'Choose who can post on each of your social media channels',
      'Collaborate on ideas with your team',
      'Stay on top of performance with automated reports',
    ],
    imageUrl:
      'https://buffer-publish.s3.us-east-1.amazonaws.com/images/wecome-team.png',
    imageAlt: 'Approvals feature',
  },
  queueLimit: {
    title: 'Try unlimited posts with Buffer, free for 14 days',
    description: 'With an upgraded Buffer plan, you can:',
    benefits: [
      'Schedule unlimited posts across all your channels',
      'Plan your content strategy weeks or months in advance',
      'Maintain consistent posting schedules without limits',
      'Access advanced scheduling and analytics',
    ],
    imageUrl:
      'https://buffer-publish.s3.us-east-1.amazonaws.com/images/welcome-essentials.png',
    imageAlt: 'Queue Limit feature',
  },
  channelLimit: {
    title: 'Try more channels with Buffer, free for 14 days',
    description: 'With an upgraded Buffer plan, you can:',
    benefits: [
      'Connect and manage more than 3 social channels',
      'Collaborate with your team across all platforms',
      'Access advanced scheduling and analytics',
      'Scale your social media presence effortlessly',
    ],
    imageUrl:
      'https://buffer-ui.s3.amazonaws.com/illustrations/add-channels.png',
    imageAlt: 'Channel Limit feature',
  },
}

export const FeatureTrialModal = (): JSX.Element => {
  const user = useUser()
  const { modalData, openModal, dismissModal: closeAction } = useModalManager()
  const {
    feature = 'approvals',
    cta,
    upgradePathName,
  } = (modalData || {}) as {
    feature?: FeatureType
    cta?: string
    upgradePathName?: string
  }

  const featureConfig = featureConfigs[feature]

  const { startTrial, trial, error, processing } = useStartTrial({
    user,
    attribution: {
      cta: prefixWithProduct(cta),
      upgradePathName: upgradePathName || '',
    },
  })

  // Mechanism to dismiss from appearing to a customer who
  // has signed up for free and starts a trial
  const { onDismissObject } = useDismissObject({
    organizationId: user.currentOrganization?.id || '',
    dismissedObject: {
      id: 'trial_signup_welcome_modal',
    },
  })

  // Handle trial success
  useEffect(() => {
    if (trial?.__typename === 'StartTrialResponse') {
      onDismissObject()
      closeAction()
      openModal({
        key: MODALS.success,
        data: {
          startedTrial: true,
        },
      })
    }
  }, [trial, onDismissObject, openModal, closeAction])

  // Track modal viewed on mount
  useEffect(() => {
    trackFeatureTrialModalViewed({
      payload: {
        feature,
        cta: cta || '',
        upgradePathName: upgradePathName || '',
      },
      user,
    })
  }, [])

  const handleOnDismissModal = (): void => {
    closeAction()
  }

  const handleStartTrial = (): void => {
    startTrial()

    // Track CTA click using standard pattern
    const canPerformTracking = shouldPerformTrackingEvent({ user })
    if (canPerformTracking) {
      const organization = getOrganization(user)
      const product = getActiveProductFromPath()
      const compoundCta = prefixWithProduct(cta)

      const commonTrackingProperties =
        getCommonTrackingPropertiesFromAccount(user)

      BufferTracker.cTAClicked({
        organizationId: organization?.id,
        cta: compoundCta,
        upgradePathName: upgradePathName || '',
        clientName: 'publishWeb',
        product,
        ...commonTrackingProperties,
      })
    }
  }

  return (
    <UpgradeDialog open={true} onOpenChange={handleOnDismissModal}>
      <UpgradeDialog.Content
        className={
          feature === 'channelLimit' ? styles.channelLimitModal : undefined
        }
      >
        <UpgradeDialog.TextContent>
          <UpgradeDialog.Title>{featureConfig.title}</UpgradeDialog.Title>
          <UpgradeDialog.Description>
            {featureConfig.description}
          </UpgradeDialog.Description>
          <List>
            {featureConfig.benefits.map((benefit: string) => (
              <List.Item key={benefit} icon={CheckIcon}>
                <Text as="span">{benefit}</Text>
              </List.Item>
            ))}
          </List>
          <Button
            variant="primary"
            size="large"
            disabled={processing}
            onClick={handleStartTrial}
          >
            {processing ? 'Processing ...' : 'Start Your Free 14-day Trial'}
          </Button>
          <Text as="p" color="subtle">
            No credit card required
          </Text>
          {error && <ErrorMessage isInline={false} error={error} />}
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image alt={featureConfig.imageAlt} src={featureConfig.imageUrl} />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
